// Page de retour du flow Google OAuth.
// Le backend redirige ici avec :
//   - hash fragment #access=...&refresh=...&email=...&userId=...&tenantId=...&role=...
//     (cas succès — le hash n'est jamais envoyé au serveur)
//   - query string ?error=... (cas échec)

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const ERROR_MESSAGES: Record<string, string> = {
  state_mismatch: 'Sécurité : la session de connexion a expiré. Réessaie.',
  missing_params: 'Réponse Google incomplète. Réessaie.',
  access_denied: "Tu as refusé d'autoriser Falko sur ton compte Google.",
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 1. Tentative erreur via query string
    const params = new URLSearchParams(window.location.search);
    const errKey = params.get('error');
    if (errKey) {
      setError(ERROR_MESSAGES[errKey] ?? `Erreur Google : ${errKey}`);
      return;
    }

    // 2. Succès : tokens dans le hash fragment
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const accessToken = hashParams.get('access');
    const refreshToken = hashParams.get('refresh');
    const userId = hashParams.get('userId');
    const email = hashParams.get('email');
    const tenantId = hashParams.get('tenantId');
    const role = hashParams.get('role');

    if (!accessToken || !refreshToken || !userId || !email || !tenantId || !role) {
      setError('Paramètres manquants dans la réponse du serveur.');
      return;
    }

    setSession({
      user: { id: userId, email, tenantId, role },
      tokens: { accessToken, refreshToken },
    });

    // Nettoie l'URL avant de naviguer
    window.history.replaceState({}, '', '/auth/callback');
    navigate('/dashboard', { replace: true });
  }, [navigate, setSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-4xl font-semibold text-neutral-900 mb-8">Falko</h1>
        {error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
            <div className="text-3xl mb-2">⚠️</div>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="rounded-lg bg-violet-600 text-white py-2 px-4 text-sm font-medium hover:bg-violet-700 transition"
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
            <div className="text-3xl mb-2">⏳</div>
            <p className="text-sm text-neutral-600">Finalisation de la connexion Google…</p>
          </div>
        )}
      </div>
    </div>
  );
};
